<!--
  Forklift Inspection Form
  
  Step-by-step daily inspection with:
  - 3 steps: Engine & Power Systems, Mechanical & Safety Systems, Sign-off
  - Comprehensive equipment inspection checklist with Yes/No/N/A options
  - Optional comment fields for each inspection item
  - Form validation for safety compliance
  - Signature requirements on final step
-->

<!-- Use the form layout component to handle the wizard UI -->
<app-form-layout
  [title]="formTitle"
  [form]="form"
  [icon]="formIcon"
  [disabled]="submitting"
  [onSubmit]="onSubmit.bind(this)"
  [steps]="steps"
  [currentStep]="currentStep()"
  [completedSteps]="completedSteps()"
  [canNavigateToStep]="canNavigateToStep.bind(this)"
  [isStepCompleted]="isStepCompleted.bind(this)"
  [onNextStep]="onNextStep.bind(this)"
  [onPreviousStep]="onPreviousStep.bind(this)"
  [getProgressPercentage]="getProgressPercentage.bind(this)"
>
  <div
    *ngIf="form && fieldMappings"
    formContent
    [formGroup]="form"
    class="form-content"
  >
    <!-- Engine & Power Systems Step -->
    <div *ngIf="currentStep() === steps[0].id" class="step-content">
      <ion-list>
        <ion-item-divider class="form-step-title">
          <ion-label>
            <h1>{{ steps[0].title }}</h1>
            <p>{{ steps[0].description }}</p>
          </ion-label>
        </ion-item-divider>

        <!-- Basic Information Section -->
        <ion-item-divider class="form-section-title">
          <ion-label>
            <h2>{{ steps[0].sections?.[0]?.title }}</h2>
            <p>{{ steps[0].sections?.[0]?.description }}</p>
          </ion-label>
        </ion-item-divider>

        <ion-row>
          <ion-col size="12" size-md="6">
            <app-date-input
              [templateFormGroup]="enginePowerFormGroup"
              [control]="enginePowerFormGroup.get('date')!"
              [label]="fieldMappings['date']"
              [requiredField]="requiredFields['date']"
              [today]="true"
            >
            </app-date-input>
          </ion-col>
          <ion-col size="12" size-md="6">
            <app-text-input
              [templateFormGroup]="enginePowerFormGroup"
              [control]="enginePowerFormGroup.get('jobNameNumber')!"
              [label]="fieldMappings['jobNameNumber']"
              [requiredField]="requiredFields['jobNameNumber']"
              placeholder="Enter job name/number"
            >
            </app-text-input>
          </ion-col>
        </ion-row>

        <!-- Engine & Power Systems Section -->
        <ion-item-divider class="form-section-title">
          <ion-label>
            <h2>{{ steps[0].sections?.[1]?.title }}</h2>
            <p>{{ steps[0].sections?.[1]?.description }}</p>
          </ion-label>
        </ion-item-divider>

        <ion-grid>
          <!-- Battery -->
          <ion-row>
            <ion-col size="12" size-md="6">
              <app-radio-group-input
                [templateFormGroup]="enginePowerFormGroup"
                [control]="enginePowerFormGroup.get('battery')!"
                [label]="fieldMappings['battery']"
                [requiredField]="requiredFields['battery']"
                [options]="['Yes', 'No', 'N/A']"
                [orientation]="'horizontal'"
              >
              </app-radio-group-input>
            </ion-col>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="enginePowerFormGroup"
                [control]="enginePowerFormGroup.get('batteryComments')!"
                [label]="fieldMappings['batteryComments']"
                placeholder="Enter comments (optional)"
              >
              </app-text-input>
            </ion-col>
          </ion-row>

          <!-- Engine Oil -->
          <ion-row>
            <ion-col size="12" size-md="6">
              <app-radio-group-input
                [templateFormGroup]="enginePowerFormGroup"
                [control]="enginePowerFormGroup.get('engineOil')!"
                [label]="fieldMappings['engineOil']"
                [requiredField]="requiredFields['engineOil']"
                [options]="['Yes', 'No', 'N/A']"
                [orientation]="'horizontal'"
              >
              </app-radio-group-input>
            </ion-col>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="enginePowerFormGroup"
                [control]="enginePowerFormGroup.get('engineOilComments')!"
                [label]="fieldMappings['engineOilComments']"
                placeholder="Enter comments (optional)"
              >
              </app-text-input>
            </ion-col>
          </ion-row>

          <!-- Coolant -->
          <ion-row>
            <ion-col size="12" size-md="6">
              <app-radio-group-input
                [templateFormGroup]="enginePowerFormGroup"
                [control]="enginePowerFormGroup.get('coolant')!"
                [label]="fieldMappings['coolant']"
                [requiredField]="requiredFields['coolant']"
                [options]="['Yes', 'No', 'N/A']"
                [orientation]="'horizontal'"
              >
              </app-radio-group-input>
            </ion-col>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="enginePowerFormGroup"
                [control]="enginePowerFormGroup.get('coolantComments')!"
                [label]="fieldMappings['coolantComments']"
                placeholder="Enter comments (optional)"
              >
              </app-text-input>
            </ion-col>
          </ion-row>

          <!-- Hydraulic Fluid -->
          <ion-row>
            <ion-col size="12" size-md="6">
              <app-radio-group-input
                [templateFormGroup]="enginePowerFormGroup"
                [control]="enginePowerFormGroup.get('hydraulicFluid')!"
                [label]="fieldMappings['hydraulicFluid']"
                [requiredField]="requiredFields['hydraulicFluid']"
                [options]="['Yes', 'No', 'N/A']"
                [orientation]="'horizontal'"
              >
              </app-radio-group-input>
            </ion-col>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="enginePowerFormGroup"
                [control]="enginePowerFormGroup.get('hydraulicFluidComments')!"
                [label]="fieldMappings['hydraulicFluidComments']"
                placeholder="Enter comments (optional)"
              >
              </app-text-input>
            </ion-col>
          </ion-row>

          <!-- Hydraulic Lines -->
          <ion-row>
            <ion-col size="12" size-md="6">
              <app-radio-group-input
                [templateFormGroup]="enginePowerFormGroup"
                [control]="enginePowerFormGroup.get('hydraulicLines')!"
                [label]="fieldMappings['hydraulicLines']"
                [requiredField]="requiredFields['hydraulicLines']"
                [options]="['Yes', 'No', 'N/A']"
                [orientation]="'horizontal'"
              >
              </app-radio-group-input>
            </ion-col>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="enginePowerFormGroup"
                [control]="enginePowerFormGroup.get('hydraulicLinesComments')!"
                [label]="fieldMappings['hydraulicLinesComments']"
                placeholder="Enter comments (optional)"
              >
              </app-text-input>
            </ion-col>
          </ion-row>

          <!-- Transmission Fluid -->
          <ion-row>
            <ion-col size="12" size-md="6">
              <app-radio-group-input
                [templateFormGroup]="enginePowerFormGroup"
                [control]="enginePowerFormGroup.get('transmissionFluid')!"
                [label]="fieldMappings['transmissionFluid']"
                [requiredField]="requiredFields['transmissionFluid']"
                [options]="['Yes', 'No', 'N/A']"
                [orientation]="'horizontal'"
              >
              </app-radio-group-input>
            </ion-col>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="enginePowerFormGroup"
                [control]="enginePowerFormGroup.get('transmissionFluidComments')!"
                [label]="fieldMappings['transmissionFluidComments']"
                placeholder="Enter comments (optional)"
              >
              </app-text-input>
            </ion-col>
          </ion-row>

          <!-- Propane Tank -->
          <ion-row>
            <ion-col size="12" size-md="6">
              <app-radio-group-input
                [templateFormGroup]="enginePowerFormGroup"
                [control]="enginePowerFormGroup.get('propaneTank')!"
                [label]="fieldMappings['propaneTank']"
                [requiredField]="requiredFields['propaneTank']"
                [options]="['Yes', 'No', 'N/A']"
                [orientation]="'horizontal'"
              >
              </app-radio-group-input>
            </ion-col>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="enginePowerFormGroup"
                [control]="enginePowerFormGroup.get('propaneTankComments')!"
                [label]="fieldMappings['propaneTankComments']"
                placeholder="Enter comments (optional)"
              >
              </app-text-input>
            </ion-col>
          </ion-row>

          <!-- Propane Hose -->
          <ion-row>
            <ion-col size="12" size-md="6">
              <app-radio-group-input
                [templateFormGroup]="enginePowerFormGroup"
                [control]="enginePowerFormGroup.get('propaneHose')!"
                [label]="fieldMappings['propaneHose']"
                [requiredField]="requiredFields['propaneHose']"
                [options]="['Yes', 'No', 'N/A']"
                [orientation]="'horizontal'"
              >
              </app-radio-group-input>
            </ion-col>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="enginePowerFormGroup"
                [control]="enginePowerFormGroup.get('propaneHoseComments')!"
                [label]="fieldMappings['propaneHoseComments']"
                placeholder="Enter comments (optional)"
              >
              </app-text-input>
            </ion-col>
          </ion-row>

          <!-- Horn -->
          <ion-row>
            <ion-col size="12" size-md="6">
              <app-radio-group-input
                [templateFormGroup]="enginePowerFormGroup"
                [control]="enginePowerFormGroup.get('horn')!"
                [label]="fieldMappings['horn']"
                [requiredField]="requiredFields['horn']"
                [options]="['Yes', 'No', 'N/A']"
                [orientation]="'horizontal'"
              >
              </app-radio-group-input>
            </ion-col>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="enginePowerFormGroup"
                [control]="enginePowerFormGroup.get('hornComments')!"
                [label]="fieldMappings['hornComments']"
                placeholder="Enter comments (optional)"
              >
              </app-text-input>
            </ion-col>
          </ion-row>

          <!-- Lights -->
          <ion-row>
            <ion-col size="12" size-md="6">
              <app-radio-group-input
                [templateFormGroup]="enginePowerFormGroup"
                [control]="enginePowerFormGroup.get('lights')!"
                [label]="fieldMappings['lights']"
                [requiredField]="requiredFields['lights']"
                [options]="['Yes', 'No', 'N/A']"
                [orientation]="'horizontal'"
              >
              </app-radio-group-input>
            </ion-col>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="enginePowerFormGroup"
                [control]="enginePowerFormGroup.get('lightsComments')!"
                [label]="fieldMappings['lightsComments']"
                placeholder="Enter comments (optional)"
              >
              </app-text-input>
            </ion-col>
          </ion-row>

          <!-- Reverse Beeper -->
          <ion-row>
            <ion-col size="12" size-md="6">
              <app-radio-group-input
                [templateFormGroup]="enginePowerFormGroup"
                [control]="enginePowerFormGroup.get('reverseBeeper')!"
                [label]="fieldMappings['reverseBeeper']"
                [requiredField]="requiredFields['reverseBeeper']"
                [options]="['Yes', 'No', 'N/A']"
                [orientation]="'horizontal'"
              >
              </app-radio-group-input>
            </ion-col>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="enginePowerFormGroup"
                [control]="enginePowerFormGroup.get('reverseBeeperComments')!"
                [label]="fieldMappings['reverseBeeperComments']"
                placeholder="Enter comments (optional)"
              >
              </app-text-input>
            </ion-col>
          </ion-row>
        </ion-grid>
      </ion-list>
    </div>

    <!-- Mechanical & Safety Systems Step -->
    <div *ngIf="currentStep() === steps[1].id" class="step-content">
      <ion-list>
        <ion-item-divider class="form-step-title">
          <ion-label>
            <h1>{{ steps[1].title }}</h1>
            <p>{{ steps[1].description }}</p>
          </ion-label>
        </ion-item-divider>

        <ion-item-divider class="form-section-title">
          <ion-label>
            <h2>{{ steps[1].sections?.[0]?.title }}</h2>
            <p>{{ steps[1].sections?.[0]?.description }}</p>
          </ion-label>
        </ion-item-divider>



        <ion-grid>
          <!-- Forks -->
          <ion-row>
            <ion-col size="12" size-md="6">
              <app-radio-group-input
                [templateFormGroup]="mechanicalSafetyFormGroup"
                [control]="mechanicalSafetyFormGroup.get('forks')!"
                [label]="fieldMappings['forks']"
                [requiredField]="requiredFields['forks']"
                [options]="['Yes', 'No', 'N/A']"
                [orientation]="'horizontal'"
              >
              </app-radio-group-input>
            </ion-col>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="mechanicalSafetyFormGroup"
                [control]="mechanicalSafetyFormGroup.get('forksComments')!"
                [label]="fieldMappings['forksComments']"
                placeholder="Enter comments (optional)"
              >
              </app-text-input>
            </ion-col>
          </ion-row>

          <!-- Carriage -->
          <ion-row>
            <ion-col size="12" size-md="6">
              <app-radio-group-input
                [templateFormGroup]="mechanicalSafetyFormGroup"
                [control]="mechanicalSafetyFormGroup.get('carriage')!"
                [label]="fieldMappings['carriage']"
                [requiredField]="requiredFields['carriage']"
                [options]="['Yes', 'No', 'N/A']"
                [orientation]="'horizontal'"
              >
              </app-radio-group-input>
            </ion-col>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="mechanicalSafetyFormGroup"
                [control]="mechanicalSafetyFormGroup.get('carriageComments')!"
                [label]="fieldMappings['carriageComments']"
                placeholder="Enter comments (optional)"
              >
              </app-text-input>
            </ion-col>
          </ion-row>

          <!-- Lift Chains/Rollers -->
          <ion-row>
            <ion-col size="12" size-md="6">
              <app-radio-group-input
                [templateFormGroup]="mechanicalSafetyFormGroup"
                [control]="mechanicalSafetyFormGroup.get('liftChainsRollers')!"
                [label]="fieldMappings['liftChainsRollers']"
                [requiredField]="requiredFields['liftChainsRollers']"
                [options]="['Yes', 'No', 'N/A']"
                [orientation]="'horizontal'"
              >
              </app-radio-group-input>
            </ion-col>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="mechanicalSafetyFormGroup"
                [control]="mechanicalSafetyFormGroup.get('liftChainsRollersComments')!"
                [label]="fieldMappings['liftChainsRollersComments']"
                placeholder="Enter comments (optional)"
              >
              </app-text-input>
            </ion-col>
          </ion-row>

          <!-- Raise/Lower -->
          <ion-row>
            <ion-col size="12" size-md="6">
              <app-radio-group-input
                [templateFormGroup]="mechanicalSafetyFormGroup"
                [control]="mechanicalSafetyFormGroup.get('raiseLower')!"
                [label]="fieldMappings['raiseLower']"
                [requiredField]="requiredFields['raiseLower']"
                [options]="['Yes', 'No', 'N/A']"
                [orientation]="'horizontal'"
              >
              </app-radio-group-input>
            </ion-col>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="mechanicalSafetyFormGroup"
                [control]="mechanicalSafetyFormGroup.get('raiseLowerComments')!"
                [label]="fieldMappings['raiseLowerComments']"
                placeholder="Enter comments (optional)"
              >
              </app-text-input>
            </ion-col>
          </ion-row>

          <!-- Tilt -->
          <ion-row>
            <ion-col size="12" size-md="6">
              <app-radio-group-input
                [templateFormGroup]="mechanicalSafetyFormGroup"
                [control]="mechanicalSafetyFormGroup.get('tilt')!"
                [label]="fieldMappings['tilt']"
                [requiredField]="requiredFields['tilt']"
                [options]="['Yes', 'No', 'N/A']"
                [orientation]="'horizontal'"
              >
              </app-radio-group-input>
            </ion-col>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="mechanicalSafetyFormGroup"
                [control]="mechanicalSafetyFormGroup.get('tiltComments')!"
                [label]="fieldMappings['tiltComments']"
                placeholder="Enter comments (optional)"
              >
              </app-text-input>
            </ion-col>
          </ion-row>

          <!-- Side Shift -->
          <ion-row>
            <ion-col size="12" size-md="6">
              <app-radio-group-input
                [templateFormGroup]="mechanicalSafetyFormGroup"
                [control]="mechanicalSafetyFormGroup.get('sideShift')!"
                [label]="fieldMappings['sideShift']"
                [requiredField]="requiredFields['sideShift']"
                [options]="['Yes', 'No', 'N/A']"
                [orientation]="'horizontal'"
              >
              </app-radio-group-input>
            </ion-col>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="mechanicalSafetyFormGroup"
                [control]="mechanicalSafetyFormGroup.get('sideShiftComments')!"
                [label]="fieldMappings['sideShiftComments']"
                placeholder="Enter comments (optional)"
              >
              </app-text-input>
            </ion-col>
          </ion-row>

          <!-- Steering -->
          <ion-row>
            <ion-col size="12" size-md="6">
              <app-radio-group-input
                [templateFormGroup]="mechanicalSafetyFormGroup"
                [control]="mechanicalSafetyFormGroup.get('steering')!"
                [label]="fieldMappings['steering']"
                [requiredField]="requiredFields['steering']"
                [options]="['Yes', 'No', 'N/A']"
                [orientation]="'horizontal'"
              >
              </app-radio-group-input>
            </ion-col>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="mechanicalSafetyFormGroup"
                [control]="mechanicalSafetyFormGroup.get('steeringComments')!"
                [label]="fieldMappings['steeringComments']"
                placeholder="Enter comments (optional)"
              >
              </app-text-input>
            </ion-col>
          </ion-row>

          <!-- Brakes -->
          <ion-row>
            <ion-col size="12" size-md="6">
              <app-radio-group-input
                [templateFormGroup]="mechanicalSafetyFormGroup"
                [control]="mechanicalSafetyFormGroup.get('brakes')!"
                [label]="fieldMappings['brakes']"
                [requiredField]="requiredFields['brakes']"
                [options]="['Yes', 'No', 'N/A']"
                [orientation]="'horizontal'"
              >
              </app-radio-group-input>
            </ion-col>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="mechanicalSafetyFormGroup"
                [control]="mechanicalSafetyFormGroup.get('brakesComments')!"
                [label]="fieldMappings['brakesComments']"
                placeholder="Enter comments (optional)"
              >
              </app-text-input>
            </ion-col>
          </ion-row>

          <!-- Tires -->
          <ion-row>
            <ion-col size="12" size-md="6">
              <app-radio-group-input
                [templateFormGroup]="mechanicalSafetyFormGroup"
                [control]="mechanicalSafetyFormGroup.get('tires')!"
                [label]="fieldMappings['tires']"
                [requiredField]="requiredFields['tires']"
                [options]="['Yes', 'No', 'N/A']"
                [orientation]="'horizontal'"
              >
              </app-radio-group-input>
            </ion-col>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="mechanicalSafetyFormGroup"
                [control]="mechanicalSafetyFormGroup.get('tiresComments')!"
                [label]="fieldMappings['tiresComments']"
                placeholder="Enter comments (optional)"
              >
              </app-text-input>
            </ion-col>
          </ion-row>

          <!-- Main Body of Lift Truck -->
          <ion-row>
            <ion-col size="12" size-md="6">
              <app-radio-group-input
                [templateFormGroup]="mechanicalSafetyFormGroup"
                [control]="mechanicalSafetyFormGroup.get('mainBodyOfLiftTruck')!"
                [label]="fieldMappings['mainBodyOfLiftTruck']"
                [requiredField]="requiredFields['mainBodyOfLiftTruck']"
                [options]="['Yes', 'No', 'N/A']"
                [orientation]="'horizontal'"
              >
              </app-radio-group-input>
            </ion-col>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="mechanicalSafetyFormGroup"
                [control]="mechanicalSafetyFormGroup.get('mainBodyOfLiftTruckComments')!"
                [label]="fieldMappings['mainBodyOfLiftTruckComments']"
                placeholder="Enter comments (optional)"
              >
              </app-text-input>
            </ion-col>
          </ion-row>

          <!-- Overhead Guard -->
          <ion-row>
            <ion-col size="12" size-md="6">
              <app-radio-group-input
                [templateFormGroup]="mechanicalSafetyFormGroup"
                [control]="mechanicalSafetyFormGroup.get('overheadGuard')!"
                [label]="fieldMappings['overheadGuard']"
                [requiredField]="requiredFields['overheadGuard']"
                [options]="['Yes', 'No', 'N/A']"
                [orientation]="'horizontal'"
              >
              </app-radio-group-input>
            </ion-col>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="mechanicalSafetyFormGroup"
                [control]="mechanicalSafetyFormGroup.get('overheadGuardComments')!"
                [label]="fieldMappings['overheadGuardComments']"
                placeholder="Enter comments (optional)"
              >
              </app-text-input>
            </ion-col>
          </ion-row>

          <!-- Bolts/Pins -->
          <ion-row>
            <ion-col size="12" size-md="6">
              <app-radio-group-input
                [templateFormGroup]="mechanicalSafetyFormGroup"
                [control]="mechanicalSafetyFormGroup.get('boltsPins')!"
                [label]="fieldMappings['boltsPins']"
                [requiredField]="requiredFields['boltsPins']"
                [options]="['Yes', 'No', 'N/A']"
                [orientation]="'horizontal'"
              >
              </app-radio-group-input>
            </ion-col>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="mechanicalSafetyFormGroup"
                [control]="mechanicalSafetyFormGroup.get('boltsPinsComments')!"
                [label]="fieldMappings['boltsPinsComments']"
                placeholder="Enter comments (optional)"
              >
              </app-text-input>
            </ion-col>
          </ion-row>

          <!-- Boom Indicator -->
          <ion-row>
            <ion-col size="12" size-md="6">
              <app-radio-group-input
                [templateFormGroup]="mechanicalSafetyFormGroup"
                [control]="mechanicalSafetyFormGroup.get('boomIndicator')!"
                [label]="fieldMappings['boomIndicator']"
                [requiredField]="requiredFields['boomIndicator']"
                [options]="['Yes', 'No', 'N/A']"
                [orientation]="'horizontal'"
              >
              </app-radio-group-input>
            </ion-col>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="mechanicalSafetyFormGroup"
                [control]="mechanicalSafetyFormGroup.get('boomIndicatorComments')!"
                [label]="fieldMappings['boomIndicatorComments']"
                placeholder="Enter comments (optional)"
              >
              </app-text-input>
            </ion-col>
          </ion-row>

          <!-- Glass/Mirrors -->
          <ion-row>
            <ion-col size="12" size-md="6">
              <app-radio-group-input
                [templateFormGroup]="mechanicalSafetyFormGroup"
                [control]="mechanicalSafetyFormGroup.get('glassMirrors')!"
                [label]="fieldMappings['glassMirrors']"
                [requiredField]="requiredFields['glassMirrors']"
                [options]="['Yes', 'No', 'N/A']"
                [orientation]="'horizontal'"
              >
              </app-radio-group-input>
            </ion-col>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="mechanicalSafetyFormGroup"
                [control]="mechanicalSafetyFormGroup.get('glassMirrorsComments')!"
                [label]="fieldMappings['glassMirrorsComments']"
                placeholder="Enter comments (optional)"
              >
              </app-text-input>
            </ion-col>
          </ion-row>

          <!-- Seat Belts -->
          <ion-row>
            <ion-col size="12" size-md="6">
              <app-radio-group-input
                [templateFormGroup]="mechanicalSafetyFormGroup"
                [control]="mechanicalSafetyFormGroup.get('seatBelts')!"
                [label]="fieldMappings['seatBelts']"
                [requiredField]="requiredFields['seatBelts']"
                [options]="['Yes', 'No', 'N/A']"
                [orientation]="'horizontal'"
              >
              </app-radio-group-input>
            </ion-col>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="mechanicalSafetyFormGroup"
                [control]="mechanicalSafetyFormGroup.get('seatBeltsComments')!"
                [label]="fieldMappings['seatBeltsComments']"
                placeholder="Enter comments (optional)"
              >
              </app-text-input>
            </ion-col>
          </ion-row>

          <!-- Fire Extinguisher -->
          <ion-row>
            <ion-col size="12" size-md="6">
              <app-radio-group-input
                [templateFormGroup]="mechanicalSafetyFormGroup"
                [control]="mechanicalSafetyFormGroup.get('fireExtinguisher')!"
                [label]="fieldMappings['fireExtinguisher']"
                [requiredField]="requiredFields['fireExtinguisher']"
                [options]="['Yes', 'No', 'N/A']"
                [orientation]="'horizontal'"
              >
              </app-radio-group-input>
            </ion-col>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="mechanicalSafetyFormGroup"
                [control]="mechanicalSafetyFormGroup.get('fireExtinguisherComments')!"
                [label]="fieldMappings['fireExtinguisherComments']"
                placeholder="Enter comments (optional)"
              >
              </app-text-input>
            </ion-col>
          </ion-row>

          <!-- Warning Decals -->
          <ion-row>
            <ion-col size="12" size-md="6">
              <app-radio-group-input
                [templateFormGroup]="mechanicalSafetyFormGroup"
                [control]="mechanicalSafetyFormGroup.get('warningDecals')!"
                [label]="fieldMappings['warningDecals']"
                [requiredField]="requiredFields['warningDecals']"
                [options]="['Yes', 'No', 'N/A']"
                [orientation]="'horizontal'"
              >
              </app-radio-group-input>
            </ion-col>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="mechanicalSafetyFormGroup"
                [control]="mechanicalSafetyFormGroup.get('warningDecalsComments')!"
                [label]="fieldMappings['warningDecalsComments']"
                placeholder="Enter comments (optional)"
              >
              </app-text-input>
            </ion-col>
          </ion-row>

          <!-- Rigging/Shackles -->
          <ion-row>
            <ion-col size="12" size-md="6">
              <app-radio-group-input
                [templateFormGroup]="mechanicalSafetyFormGroup"
                [control]="mechanicalSafetyFormGroup.get('riggingShackles')!"
                [label]="fieldMappings['riggingShackles']"
                [requiredField]="requiredFields['riggingShackles']"
                [options]="['Yes', 'No', 'N/A']"
                [orientation]="'horizontal'"
                hjkasdhfksjdhf
              >
              </app-radio-group-input>
            </ion-col>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="mechanicalSafetyFormGroup"
                [control]="mechanicalSafetyFormGroup.get('riggingShacklesComments')!"
                [label]="fieldMappings['riggingShacklesComments']"
                placeholder="Enter comments (optional)"
              >
              </app-text-input>
            </ion-col>
          </ion-row>

          <!-- Lubricant/Fittings -->
          <ion-row>
            <ion-col size="12" size-md="6">
              <app-radio-group-input
                [templateFormGroup]="mechanicalSafetyFormGroup"
                [control]="mechanicalSafetyFormGroup.get('lubricantFittings')!"
                [label]="fieldMappings['lubricantFittings']"
                [requiredField]="requiredFields['lubricantFittings']"
                [options]="['Yes', 'No', 'N/A']"
                [orientation]="'horizontal'"
              >
              </app-radio-group-input>
            </ion-col>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="mechanicalSafetyFormGroup"
                [control]="mechanicalSafetyFormGroup.get('lubricantFittingsComments')!"
                [label]="fieldMappings['lubricantFittingsComments']"
                placeholder="Enter comments (optional)"
              >
              </app-text-input>
            </ion-col>
          </ion-row>
        </ion-grid>
      </ion-list>
    </div>

    <!-- Sign-off Step -->
    <div *ngIf="currentStep() === steps[2].id" class="step-content">
      <ion-list>
        <ion-item-divider class="form-step-title">
          <ion-label>
            <h1>{{ steps[2].title }}</h1>
            <p>{{ steps[2].description }}</p>
          </ion-label>
        </ion-item-divider>

        <ion-grid>

          <ion-row>
            <ion-col>
              <app-textarea-input
                [templateFormGroup]="signOffFormGroup"
                [control]="signOffFormGroup.get('generalComments')!"
                [label]="fieldMappings['generalComments']"
                [rows]="4"
                placeholder="Enter any additional comments (optional)"
              >
              </app-textarea-input>
            </ion-col>
          </ion-row>
          
          <ion-row>
            <ion-col>
              <app-text-input
                [templateFormGroup]="signOffFormGroup"
                [control]="signOffFormGroup.get('operatorName')!"
                [label]="fieldMappings['operatorName']"
                [requiredField]="requiredFields['operatorName']"
                placeholder="Enter operator name"
              >
              </app-text-input>
            </ion-col>
          </ion-row>
          <ion-row>
            <ion-col>
              <app-signature-section
                [signatureInfo]="formAttachmentRequirements!.signatures![0]!"
              ></app-signature-section>
            </ion-col>
          </ion-row>
         
        </ion-grid>
      </ion-list>
    </div>
  </div>
</app-form-layout>
    